import * as React from 'react'
import { DonutChartLoader, useAlert, useEffectWithPolling } from 'uxp/components'
import { IWidgetTemplateInstanceProps, IWidgetTemplateProps, registerCardTemplate } from '../../../common/common'
import { generateIconProp } from '../../../common/icon-selector/IconEditor'
import { extractContainerProperties } from '../../../common/ui-props/ContainerProperty'
import { extractTextProperties } from "../../../common/ui-props/TextProperty";
import { DataSourceProperty } from "../../../common/ui-props/DataSourceProperty";
import { Cell, Legend, Pie, PieChart, ResponsiveContainer, Tooltip, TooltipProps } from 'recharts'
import { DonutChartProperty, IDonutChartProps } from './DonutChartPropery'
import { hasValue, replaceWithData } from '../../../../util'
import { createDefaultPropsForWidgetWrapperWithSidebar, WidgetWrapperWithSidebar } from '../../../common/widget-wrappers/WidgetWrapperWithSidebar'
import { RoundOffProperty, roundOffValue } from '../../../common/ui-props/RoundOffProperty'
import { IContextData, appendRowToContextData, useInitialContextData } from '../../../../hooks/useContextData'
import { IAction } from '../../../common/action-editor/ActionEditor'
import WDFilterPanel from '../../../common/components/WDFilterPanel'
import ActionRenderComponent from '../../../common/components/ActionRenderComponent'
import tinycolor from 'tinycolor2'
import './styles.scss'
import { executeAndBind, processInitialSource } from '../../../common/data-source-picker/datasource'

let rdKey: any = {};
function generateRandomData(colors: string[], labelField: string, valueField: string, mapLabelsToColors?: boolean, labelColors?: { label: string, color: string }[]) {
    let key = JSON.stringify(mapLabelsToColors ? labelColors.map(c => (c.color)) : colors) + '/' + valueField + '/' + labelField;
    if (!rdKey[key]) {
        if (mapLabelsToColors) {
            const data = labelColors.map((c, i) => {
                return {
                    [labelField || 'name']: c.label,
                    name: c.label,
                    [valueField || 'value']: (Math.round(Math.random() * 100)),
                    color: c.color
                }
            });
            rdKey[key] = data
        }
        else {
            const data = colors.map((c, i) => {
                return {
                    [labelField || 'name']: `Label ${i + 1}`,
                    name: `Label ${i + 1}`,
                    [valueField || 'value']: (Math.round(Math.random() * 100))
                }
            });
            rdKey[key] = data
        }
    }
    return rdKey[key]
}

function sameColors(col1: string, col2: string) {

    const color1 = tinycolor(col1)
    const color2 = tinycolor(col2)

    const isSame = color1.toHex8String() == color2.toHex8String()
    return isSame
}

function isColorIncluded(colors: string[], color: string) {
    const ext = colors.find(c => sameColors(c, color))
    return !!ext
}

export type ChartSorting = 'none' | 'by-label' | 'by-value' | 'by-datetime'

function sortChartData(chartData: any[], sorting: ChartSorting, labelField: string, valueField: string, roundOffOptions?: any) {
    if (!Array.isArray(chartData)) return []

    chartData = [...chartData]

    if (sorting === 'none' || !sorting) {
        return chartData;
    }

    chartData.sort((a, b) => {
        let A: any = null;
        let B: any = null;
        if (sorting === 'by-label') {
            A = String(a[labelField])
            B = String(b[labelField])

        } else if (sorting === 'by-value') {
            A = Number(a[valueField])
            B = Number(b[valueField])
        }

        if (A < B) return -1;
        if (A > B) return 1;

        return 0;
    });

    return chartData;
}

const XPR: React.ForwardRefExoticComponent<React.RefAttributes<IWidgetTemplateInstanceProps> & IWidgetTemplateProps> = React.forwardRef((props, ref) => {
    let uiProps = props?.uiProps || {};
    const alerts = useAlert()
    const forceRerender = React.useRef<number>(0)

    const [loading, setLoading] = React.useState<boolean|null>(null)
    const [actionData, setActionData] = React.useState<{ action: IAction, contextData: IContextData }>({ action: null, contextData: {} })
    const [contextData, setContextData] = React.useState<IContextData>({})
    const [chartData, setChartData] = React.useState<any>({})
    const [initialSource, setIntitialSource] = React.useState(props.initialSource);

    const containerRef = React.useRef(null)
    const { isLoading, initialContextData } = useInitialContextData(props, [uiProps?.source])


    const defaults: IDonutChartProps = {
        labelField: '',
        valueField: '',
        sorting: 'none',
        chartSize: 80,
        legend: true,
        colors: ['#0088FEx', '#00C49F', '#FFBB28', '#FF8042'],
        type: 'pie',
        refreshInsterval: 60000,
        channel: '',
        showPercentage: false,
        useColorsForLabels: false,
        onClickChart: null,
        refreshChartAutoMatically: true
    }
    const XPRProps = createDefaultPropsForWidgetWrapperWithSidebar({
        'source': DataSourceProperty('Source', uiProps.source, uiProps.datasources, props.uxpContext, props.designer, 'dictionary-array'),
        'chartProperty': DonutChartProperty('Chart', uiProps.chartProperty || defaults),
        'roundOff': RoundOffProperty({ integer: false, decimalPoints: 2 })
    });



    const { styles: headerStyles } = extractContainerProperties(uiProps.headerContainer)
    const { text: title, styles: titleStyles } = extractTextProperties(uiProps.title, 'Pie Chart')
    let { labelField, valueField, showPercentage, legend, colors, type, refreshInsterval, chartSize, channel, sorting, customTooltipText, customLabelText, mapLabelsToColors, labelColors, onClickChart, refreshChartAutoMatically } = uiProps.chartProperty as IDonutChartProps || defaults

    const { styles: chartContainerStyles } = extractContainerProperties(uiProps.chartContainer)

    React.useImperativeHandle(ref, () => ({
        beforeSave: validateBeforeSave,
        onPreview: validateBeforeSave
    }))

    React.useEffect(() => {
        return () => {
            props.designer?.removeErrors?.(['datasource', 'field'])
        }
    }, [])

    React.useEffect(() => {
        
        if (!isLoading) {
            setContextData(prev => ({ ...prev, ...initialContextData }))
        }
    }, [isLoading])

    React.useEffect(() => {
        validateBeforeSave()
    }, [uiProps])

    React.useEffect(() => {
        processInitialSource(props.designer, props.uxpContext, props.uiProps, initialSource, 'dictionary-array')
            .then(res => {
                if (res) props.designer?.setUIProps(res)
            })
            .catch(e => {
                console.log('exception ', e);

            })
    }, [initialSource])


    useEffectWithPolling(props.uxpContext, channel || "chart/1", refreshInsterval || 60000, async () => {
        if (refreshChartAutoMatically) {
            console.log('donut_refreshing_automatically',refreshInsterval)
            loadData()
        }
    }, [props.designer?.mode, JSON.stringify(contextData)]);
  

    React.useEffect(() => {
        if (!refreshChartAutoMatically) {
            console.log('donut_loading_data')
            loadData()
        }
    }, [props.designer?.mode, JSON.stringify(contextData)]);

    const total = React.useMemo(() => {
        if (Array.isArray(chartData)) {
            return (chartData || []).reduce((total, item) => total + item?.[valueField || 'value'], 0);
        }

        return 0;
    }, [chartData])


    function loadData() {
        if (props.designer && (props.designer.mode == 'preview' || props.designer.mode == 'thumbnail')) {

            let data = generateRandomData(colors, labelField, valueField, mapLabelsToColors, labelColors);
            setChartData(data)
            return
        }
        setLoading(true)
        let params: any = { filters: contextData?.filters || {} }
        executeAndBind(props.uxpContext, uiProps.source, uiProps.datasources, params, contextData)
            .then(res => {
                let updated = res.map((r: any) => {

                    let name = r[labelField || 'name']
                    let value = r[valueField || 'value']
                    if (typeof value != 'number') {
                        //try to convert to a number 
                        value = Number(value)
                        if (isNaN(value) || !isFinite(value)) {
                            value = 0
                            console.log('value is not a number. ', r)
                        }
                    }

                    let color = null
                    if (mapLabelsToColors) {
                        const lc = labelColors.find(c => c.label == name)
                        if (lc && !!lc?.color) color = lc.color
                    }

                    return {
                        ...r,
                        name: name,
                        [valueField]: value,
                        value: value,
                        color: color
                    }
                })
                setChartData(updated)
            })
            .catch(e => {
                setChartData([])
                console.log('error getting data', e)
            })
            .finally(() => {
                setLoading(false)
            })
    }

    // this will be called before save
    function validateBeforeSave() {
        if (!isDataSourceValid()) return false
        if (!hasFieldsConfigured()) return false
        return true
    }

    function isDataSourceValid() {
        if (!hasValue(uiProps?.source) || !hasValue(uiProps.source?.source) || !hasValue(uiProps.source?.path)) {
            props.designer?.addErrors?.([{
                id: 'datasource',
                type: 'property',
                error: 'Data source is required',
                containerId: 'chart-container',
                fieldId: 'source'
            }])
            return false
        }
        props.designer?.removeErrors?.(['datasource'])
        return true
    }

    function hasFieldsConfigured() {
        if (!hasValue(uiProps?.chartProperty?.labelField)) {
            props.designer?.addErrors?.([{
                id: 'label-field',
                type: 'property',
                error: 'Label field is required',
                containerId: 'chart-container',
                fieldId: 'label-field'
            }])
            return false
        }
        props.designer?.removeErrors?.(['label-field'])

        if (!hasValue(uiProps?.chartProperty?.valueField)) {
            props.designer?.addErrors?.([{
                id: 'value-field',
                type: 'property',
                error: 'Value field is required',
                containerId: 'chart-container',
                fieldId: 'value-field'
            }])
            return false
        }
        props.designer?.removeErrors?.(['value-field'])
        return true
    }

    function getTooltipText(item: any) {
        if (!customTooltipText?.trim() || !item) return `${item?.[labelField || 'name']} : ${roundOffValue(item?.[valueField || 'value'], uiProps.roundOff)}`

        return replaceExpressions(item, customTooltipText)
    }

    function getLabel(item: any) {

        let value = roundOffValue(item?.[valueField || 'value'], uiProps.roundOff);

        if (!!customLabelText?.trim()) return replaceExpressions(item, customLabelText);

        if (showPercentage) {
            return roundOffValue(((Number(value) / total) * 100), uiProps.roundOff) + "%"
        }
        return value;
    }


    function handleChartClick() {

        if (props.designer && (props.designer.mode == 'preview' || props.designer.mode == 'thumbnail')) {
            return
        }
        setActionData({ action: onClickChart || null, contextData: { ...contextData } })
    }


    function replaceExpressions(item: any, text: string) {
        let calculated = {
            percentage: roundOffValue(((item?.[valueField || 'value'] / total) * 100), uiProps.roundOff) + "%",
            value: roundOffValue(item?.[valueField || 'value'], uiProps.roundOff)
        };

        return replaceWithData(text, { ...contextData, row: item, calculated })
    }

    let [sortedChartData, forceRender] = React.useMemo(() => {
        let sorted = sortChartData(chartData, sorting || 'none', labelField, valueField, uiProps.roundOff)

        forceRerender.current += 1
        return [sorted, forceRerender.current]
    }, [chartData, sorting, uiProps.roundOff])

    const hasSameColor = isColorIncluded(mapLabelsToColors ? labelColors.map(lc => (lc.color)) : colors, chartContainerStyles.backgroundColor)


    return <WidgetWrapperWithSidebar
        isInnerField={props?.isInnerField}
        containerRef={containerRef}
        className='uxp-custom-donut-chart-template'
        contextData={appendRowToContextData(contextData, props, uiProps.source)}
        propDef={XPRProps}
        uiProps={uiProps}
        showFilterWithoutFields={(uiProps?.filter?.enableExport == false) ? false : true}
        elementId='chart-container'
        designer={props.designer}
        defaultTitle='Pie Chart'
        filterSection={() => {
            return <WDFilterPanel
                designer={props.designer}
                uxpContext={props.uxpContext}
                uiProps={uiProps}
                containerRef={containerRef}
                data={appendRowToContextData(contextData, props, uiProps.source)}
                onChangeFilters={(data) => {
                    setContextData(prev => ({ ...prev, filters: data?.filters || {} }))
                }}
                onClearFilters={() => {
                    setContextData(prev => ({ ...prev, filters: {} }))
                }}
                onOpenPanel={() => { }}
            />
        }}>
        {
            (isLoading || (loading==null))
                ? <DonutChartLoader />
                : <ResponsiveContainer key={forceRender} >
                    <PieChart key={forceRender}
                        onClick={() => handleChartClick()}
                    >
                        <Pie
                            dataKey={valueField || 'value'}
                            labelLine={false}
                            data={sortedChartData}
                            fill="#FFFFFF00"
                            paddingAngle={1}
                            innerRadius={type == 'pie' ? 0 : `${(chartSize || 80) * 0.67}%`}
                            outerRadius={`${chartSize || 80}%`}
                            cx="50%" cy="50%"
                            isAnimationActive={false}
                            label={({
                                cx,
                                cy,
                                midAngle,
                                innerRadius,
                                outerRadius,
                                value,
                                name,
                                index,
                            }) => {
                                const RADIAN = Math.PI / 180;
                                const radius = 20 + Number(innerRadius) + (Number(outerRadius) - Number(innerRadius));
                                const x = Number(cx) + radius * Math.cos(-midAngle * RADIAN);
                                const y = Number(cy) + radius * Math.sin(-midAngle * RADIAN);
                                let color = chartContainerStyles.color;


                                if (!!uiProps?.chartProperty?.useColorsForLabels) {
                                    if (mapLabelsToColors) color = sortedChartData[index].color
                                    else color = colors[index % colors.length];
                                }
                                return (
                                    <text
                                        x={x}
                                        y={y}
                                        fill={color}
                                        textAnchor={x > Number(cx) ? "start" : "end"}
                                        dominantBaseline="central"
                                    >
                                        {getLabel(sortedChartData[index])}
                                    </text>
                                );
                            }}
                        >
                            {sortedChartData.map((entry: any, index: number) => {
                                let _color = colors[index % colors.length]
                                if (mapLabelsToColors) {
                                    _color = entry.color
                                }
                                return (
                                    <Cell stroke={'0'}
                                        style={{
                                            filter: hasSameColor ? `drop-shadow(1px 2px 10px #00000055)` : ''
                                        }}
                                        fill={_color}
                                    />
                                )
                            })}
                        </Pie>
                        <Tooltip content={(props: TooltipProps) => {
                            return <div className='wd-chart-tooltip'>
                                <div className="label">{getTooltipText(props?.payload?.[0]?.payload)}</div>
                            </div>
                        }} />
                        {legend && <Legend />}
                    </PieChart>
                </ResponsiveContainer>
        }

        <ActionRenderComponent
            designer={props.designer}
            uxpContext={props.uxpContext}
            {...actionData}
            onClose={() => setActionData({ action: null, contextData: {} })}
            onReloadWidget={() => { }}
        />

    </WidgetWrapperWithSidebar>
})


registerCardTemplate({
    id: 'donut-chart',
    name: 'Donut chart',
    description: 'A pie or donut chart that shows items with values relative to each other. The widget can be configured to refresh automatically at regular intervals.',
    template: XPR,
    icon: generateIconProp('chartPie'),
    complexity: 'easy',
    preLoader: 'donut-chart',
    expectedSchema: 'dictionary-array',
    canDragDropFields: true,
});
